"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { isSuccess } from "../../../models/response/base-response";
import DateInputCustom from "../../../components/custom/date-input/date-input-custom";
import BranchService, { Branch } from "../../branch/services/branch-service";
import RewashReportService, { RewashDrilldownKey, RewashReport } from "../services/rewash-report-service";
import RewashOrdersModal from "../components/rewash-orders-modal";

const numberFmt = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const fmt = (v: number) => numberFmt.format(Number(v) || 0);
const money = (v: number) => `${fmt(v)}đ`;
// Hiện đúng số: 2 → "2", 4.5 → "4.5", 2.25 → "2.25" (BE đã làm tròn 2 chữ số).
const weightFmt = new Intl.NumberFormat("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const kg = (v: number) => `${weightFmt.format(Number(v) || 0)} kg`;

const toDMY = (ymd: string) => {
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-");
  return d && m && y ? `${d}/${m}/${y}` : "";
};
const toYMD = (dmy: string) => {
  if (!dmy) return "";
  const [d, m, y] = dmy.split("/");
  return d && m && y ? `${y}-${m}-${d}` : "";
};
const getDefaultDates = () => {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const f = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return { from: f(first), to: f(now) };
};

const REASON_COLORS = ["#f97316", "#ef4444", "#eab308", "#8b5cf6", "#0ea5e9", "#64748b"];

function extractList(raw: any): any[] {
  if (Array.isArray(raw)) return raw;
  if (raw?.list && Array.isArray(raw.list)) return raw.list;
  if (raw?.data && Array.isArray(raw.data)) return raw.data;
  return [];
}

const RewashReportPage: React.FC = () => {
  const defaults = getDefaultDates();
  const [fromDate, setFromDate] = useState(defaults.from);
  const [toDate, setToDate] = useState(defaults.to);
  const [branchId, setBranchId] = useState<number | "">("");
  const [detectType, setDetectType] = useState<number | "">("");
  const [branches, setBranches] = useState<Branch[]>([]);
  const [report, setReport] = useState<RewashReport | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  /** Dòng đang được bóc tách (mở modal danh sách đơn); null = đóng. */
  const [drilldown, setDrilldown] = useState<{ title: string; key: RewashDrilldownKey } | null>(null);

  const { fetch: fetchReport } = RewashReportService.report();
  const { fetch: listBranches } = BranchService.myBranches();
  const reportRef = useRef(fetchReport);
  const branchRef = useRef(listBranches);
  useEffect(() => { reportRef.current = fetchReport; }, [fetchReport]);
  useEffect(() => { branchRef.current = listBranches; }, [listBranches]);

  useEffect(() => {
    (async () => {
      const res = await branchRef.current();
      if (res && isSuccess(res)) setBranches(extractList(res.data));
    })();
  }, []);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await reportRef.current({
        from_date: fromDate,
        to_date: toDate,
        ...(branchId ? { branch_id: Number(branchId) } : {}),
        ...(detectType === 0 || detectType === 1 ? { is_pickup_laundry: Number(detectType) } : {}),
        limit: 10,
      });
      if (res && isSuccess(res)) setReport(res.data as RewashReport);
    } catch (e) {
      console.error("Failed to load rewash report:", e);
    } finally {
      setIsLoading(false);
    }
  }, [fromDate, toDate, branchId, detectType]);

  useEffect(() => { void load(); }, [load]);

  // Bộ lọc chi nhánh của cả trang phải đi kèm khi bóc tách theo lý do / khách / NV — nếu không, modal
  // hiện đơn của mọi chi nhánh trong khi con số trên bảng chỉ tính một chi nhánh.
  const openDrilldown = useCallback((title: string, key: RewashDrilldownKey) => {
    setDrilldown({ title, key: { ...(branchId ? { branch_id: Number(branchId) } : {}), ...key } });
  }, [branchId]);

  const s = report?.summary;

  // Cột chồng theo ngày: tách phát hiện ngay / sau để thấy loại nào đang tăng.
  const dailyChart = useMemo(() => {
    const rows = report?.by_date || [];
    return {
      series: [
        { name: "Phát hiện ngay", data: rows.map((r) => r.immediate) },
        { name: "Phát hiện sau", data: rows.map((r) => r.delayed) },
      ],
      options: {
        chart: { type: "bar" as const, stacked: true, toolbar: { show: false } },
        colors: ["#10b981", "#f97316"],
        xaxis: { categories: rows.map((r) => toDMY(r.day).slice(0, 5)) },
        legend: { position: "top" as const },
        dataLabels: { enabled: false },
        noData: { text: "Không có dữ liệu" },
      },
    };
  }, [report]);

  const reasonChart = useMemo(() => {
    const rows = report?.by_reason || [];
    return {
      series: rows.map((r) => r.order_count),
      options: {
        chart: { type: "donut" as const },
        labels: rows.map((r) => r.reason_label),
        colors: REASON_COLORS,
        legend: { position: "bottom" as const, fontSize: "12px" },
        dataLabels: { formatter: (val: number) => `${Math.round(val)}%` },
        tooltip: { y: { formatter: (val: number) => `${fmt(val)} đơn` } },
        noData: { text: "Không có dữ liệu" },
      },
    };
  }, [report]);

  return (
    <div className="p-4 space-y-4">
      {/* Header + filter */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-orange-600 flex items-center justify-center text-white text-lg">
            🔁
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-800">Báo cáo đồ giặt lại</h1>
            <p className="text-sm text-slate-500">
              Tỉ lệ giặt lại · lý do · chi phí · trách nhiệm nhân sự · quy về chi nhánh ĐÃ GIẶT
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Từ ngày</label>
            <DateInputCustom
              type="date" value={toDMY(fromDate)}
              onChange={(d) => setFromDate(toYMD(d ?? ""))} className="w-full"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Đến ngày</label>
            <DateInputCustom
              type="date" value={toDMY(toDate)}
              onChange={(d) => setToDate(toYMD(d ?? ""))} className="w-full"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Chi nhánh</label>
            <select
              className="w-full border rounded-lg px-3 py-2 text-sm"
              value={branchId}
              onChange={(e) => setBranchId(e.target.value ? Number(e.target.value) : "")}
            >
              <option value="">Tất cả chi nhánh</option>
              {branches.map((b: any) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Loại phát hiện</label>
            <select
              className="w-full border rounded-lg px-3 py-2 text-sm"
              value={detectType}
              onChange={(e) => setDetectType(e.target.value === "" ? "" : Number(e.target.value))}
            >
              <option value="">Tất cả</option>
              <option value={0}>Phát hiện ngay</option>
              <option value={1}>Phát hiện sau</option>
            </select>
          </div>
        </div>
      </div>

      {isLoading && (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500">
          Đang tải...
        </div>
      )}

      {!isLoading && report && s && (
        <>
          {/* Ô tổng */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Card
              label="Số đơn giặt lại"
              value={fmt(s.order_count)}
              unit="đơn"
              sub={`Ngay ${fmt(s.immediate_count)} · Sau ${fmt(s.delayed_count)}`}
            />
            <Card
              label="Tỉ lệ giặt lại (theo đơn)"
              value={s.rewash_rate != null ? `${s.rewash_rate}` : "—"}
              unit="%"
              tone="orange"
              sub={`/ ${fmt(s.completed_base_count)} đơn đã giao trong kỳ`}
            />
            <Card
              label="Tỉ lệ giặt lại (theo kg)"
              value={s.rewash_weight_rate != null ? `${s.rewash_weight_rate}` : "—"}
              unit="%"
              tone="orange"
              sub={`${kg(s.weight)} / ${kg(s.base_weight)} đã giao`}
            />
            <Card
              label="Tổng khối lượng đã giao"
              value={kg(s.base_weight)}
              unit=""
              sub={
                s.base_zero_weight_count > 0
                  ? `⚠ ${fmt(s.base_zero_weight_count)} đơn chưa có cân lần 2`
                  : "đơn thường hoàn thành trong kỳ"
              }
            />
            <Card label="Khối lượng giặt lại" value={kg(s.weight)} unit="" sub="" />
            <Card label="Miễn/giảm cho khách" value={money(s.discount)} unit="" tone="rose" sub="" />
            <Card
              label="Chuyển từ đơn gốc"
              value={money(s.moved_from_origin)}
              unit=""
              sub=""
            />
          </div>

          {/* Biểu đồ */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
              <h3 className="text-sm font-semibold text-slate-800 mb-2">Số đơn giặt lại theo ngày</h3>
              <ReactApexChart options={dailyChart.options} series={dailyChart.series} type="bar" height={280} />
            </div>
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
              <h3 className="text-sm font-semibold text-slate-800 mb-2">Tỉ trọng theo lý do</h3>
              <ReactApexChart options={reasonChart.options} series={reasonChart.series} type="donut" height={280} />
            </div>
          </div>

          {/* Theo lý do */}
          <Section title="Theo lý do" hint="Nhóm lý do NV chọn khi tạo phiếu giặt lại. Đơn cũ (trước khi có danh mục) nằm ở nhóm 'Chưa phân loại'.">
            <SimpleTable
              head={["Lý do", "Số đơn", "Giá trị (gross)", "Khối lượng", "Chi tiết"]}
              rows={report.by_reason.map((r) => [
                r.reason_label,
                fmt(r.order_count),
                money(r.gross),
                kg(r.weight),
                <DetailButton
                  onClick={() =>
                    openDrilldown(r.reason_label, { reason_code: r.reason_code ?? "" })
                  }
                />,
              ])}
              empty="Chưa có đơn giặt lại trong kỳ."
            />
          </Section>

          {/* Theo chi nhánh */}
          <Section
            title="Theo chi nhánh (nơi giặt)"
            hint='"Ai giặt thì người đó chịu" — đơn được quy về chi nhánh ĐÃ GIẶT đơn gốc, không phải chi nhánh nhận khách. Đơn điều chuyển vì vậy tính cho xưởng làm, kể cả khi đơn thuộc chi nhánh khác (xem cột "Giặt hộ"). Tỉ lệ theo đơn = số đơn giặt lại / số đơn thường đã HOÀN THÀNH mà chi nhánh đó giặt trong kỳ; tỉ lệ theo kg = kg giặt lại / tổng kg đã giặt xong.'
          >
            <SimpleTable
              head={[
                "Chi nhánh",
                "Số đơn",
                "Giặt hộ",
                "Ngay / Sau",
                "Tỉ lệ (đơn)",
                "Miễn/giảm",
                "KL giặt lại",
                "Tổng KL đã giao",
                "Tỉ lệ (kg)",
                "Chi tiết",
              ]}
              rows={report.by_branch.map((r) => [
                r.branch_name,
                fmt(r.order_count),
                r.transferred_in_count > 0 ? fmt(r.transferred_in_count) : "—",
                `${fmt(r.immediate_count)} / ${fmt(r.delayed_count)}`,
                r.rewash_rate != null ? `${r.rewash_rate}%` : "—",
                money(r.discount),
                kg(r.weight),
                kg(r.base_weight),
                r.rewash_weight_rate != null ? `${r.rewash_weight_rate}%` : "—",
                <DetailButton
                  onClick={() => openDrilldown(r.branch_name, { branch_id: r.branch_id })}
                />,
              ])}
              empty="Không có dữ liệu."
            />
          </Section>

          {/* Theo NV chịu trách nhiệm */}
          <Section
            title="Theo nhân sự chịu trách nhiệm"
            hint="Chỉ tính đơn CÓ chỉ định nhân sự — hệ thống không tự gán. Vì vậy tổng ở đây nhỏ hơn tổng số đơn giặt lại."
          >
            <SimpleTable
              head={["Nhân sự", "Số đơn", "Giá trị (gross)", "Khối lượng", "Chi tiết"]}
              rows={report.by_employee.map((r) => [
                r.employee_name,
                fmt(r.order_count),
                money(r.gross),
                kg(r.weight),
                <DetailButton
                  onClick={() => openDrilldown(r.employee_name, { employee_id: r.employee_id })}
                />,
              ])}
              empty="Chưa có đơn nào được chỉ định nhân sự chịu trách nhiệm."
            />
          </Section>

          {/* Top khách */}
          <Section title="Khách hàng bị giặt lại nhiều nhất" hint="Đọc kèm sản lượng của khách — khách lớn tự nhiên có số tuyệt đối cao hơn.">
            <SimpleTable
              head={["Khách hàng", "Số đơn", "Giá trị (gross)", "Khối lượng", "Chi tiết"]}
              rows={report.by_customer.map((r) => [
                r.customer_name,
                fmt(r.order_count),
                money(r.gross),
                kg(r.weight),
                <DetailButton
                  onClick={() => openDrilldown(r.customer_name, { customer_id: r.customer_id })}
                />,
              ])}
              empty="Không có dữ liệu."
            />
          </Section>

          {/* Đơn gốc bị giặt lại nhiều lần */}
          <Section title="Đơn gốc bị giặt lại nhiều lần" hint="Điểm nóng — cùng một đơn phải giặt lại từ 2 lần trở lên.">
            <SimpleTable
              head={["Đơn gốc", "Số lần giặt lại", "Lần gần nhất"]}
              rows={report.repeated_orders.map((r) => [
                r.origin_code,
                fmt(r.times),
                r.last_at ? toDMY(r.last_at.slice(0, 10)) : "—",
              ])}
              empty="Không có đơn nào bị giặt lại quá 1 lần — tốt."
            />
          </Section>
        </>
      )}

      {drilldown && (
        <RewashOrdersModal
          title={drilldown.title}
          filterKey={drilldown.key}
          fromDate={fromDate}
          toDate={toDate}
          isPickupLaundry={detectType}
          onClose={() => setDrilldown(null)}
        />
      )}
    </div>
  );
};

/** Nút mở modal bóc tách — đặt ở cột "Chi tiết" cuối mỗi bảng tổng hợp. */
const DetailButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="rounded-md border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 whitespace-nowrap"
  >
    Xem đơn
  </button>
);

const Card: React.FC<{ label: string; value: string; unit?: string; tone?: string; sub?: string }> = ({
  label, value, unit, tone, sub,
}) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
    <div className="text-xs text-slate-500 mb-1">{label}</div>
    <div
      className={`text-2xl font-bold ${
        tone === "orange" ? "text-orange-600" : tone === "rose" ? "text-rose-600" : "text-slate-800"
      }`}
    >
      {value} <span className="text-sm font-normal text-slate-400">{unit}</span>
    </div>
    {sub && <div className="text-[11px] text-slate-400 mt-1">{sub}</div>}
  </div>
);

const Section: React.FC<{ title: string; hint?: string; children: React.ReactNode }> = ({
  title, hint, children,
}) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
    <div className="px-4 py-3 border-b">
      <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
      {hint && <p className="text-xs text-slate-500 mt-0.5">{hint}</p>}
    </div>
    {children}
  </div>
);

const SimpleTable: React.FC<{ head: string[]; rows: React.ReactNode[][]; empty: string }> = ({
  head, rows, empty,
}) => (
  <div className="overflow-x-auto">
    <table className="min-w-full text-sm">
      <thead className="bg-slate-50 text-slate-600">
        <tr>
          {head.map((h, i) => (
            <th key={h} className={`px-3 py-2 ${i === 0 ? "text-left" : "text-center"}`}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, ri) => (
          <tr key={ri} className="border-t hover:bg-slate-50">
            {r.map((c, ci) => (
              <td
                key={ci}
                className={`px-3 py-2 ${ci === 0 ? "text-left font-medium text-slate-800" : "text-center text-slate-700"}`}
              >
                {c}
              </td>
            ))}
          </tr>
        ))}
        {rows.length === 0 && (
          <tr>
            <td colSpan={head.length} className="px-3 py-8 text-center text-slate-500">{empty}</td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

export default RewashReportPage;
